import { z } from 'zod';

// ---------------------------------------------------------------------------
// Base response schemas — mirrors backend ApiResponseDto hierarchy
// ---------------------------------------------------------------------------

export const apiResponseSchema = z.object({
  errorCode: z.string().nullable().optional(),
  message: z.string(),
  isSuccess: z.boolean(),
});

export type ApiResponseDto = z.infer<typeof apiResponseSchema>;

export function apiResponseWithDataSchema<T extends z.ZodTypeAny>(dataSchema: T) {
  return apiResponseSchema.extend({
    data: dataSchema.nullable().optional(),
  });
}

export type ApiResponseWithDataDto<T> = ApiResponseDto & {
  data?: T | null;
};

export function dataCollectionResponseSchema<T extends z.ZodTypeAny>(itemSchema: T) {
  return apiResponseSchema.extend({
    items: z.array(itemSchema),
    totalCount: z.number(),
    pageNumber: z.number(),
    pageSize: z.number(),
    totalPages: z.number(),
    hasPreviousPage: z.boolean(),
    hasNextPage: z.boolean(),
  });
}

export type DataCollectionApiResponseDto<T> = ApiResponseDto & {
  items: T[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
};

// ---------------------------------------------------------------------------
// Base DTO
// ---------------------------------------------------------------------------

export const baseDtoSchema = z.object({
  id: z.string().uuid(),
  createdOnUtc: z.string(),
  editedOnUtc: z.string().nullable().optional(),
  deletedOnUtc: z.string().nullable().optional(),
});

export type BaseDto = z.infer<typeof baseDtoSchema>;

// ---------------------------------------------------------------------------
// Paged request
// ---------------------------------------------------------------------------

export const pagedRequestSchema = z.object({
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).default(20),
  searchText: z.string().nullable().optional(),
  orderColumn: z.string().nullable().optional(),
  orderDirection: z.string().nullable().optional(),
});

export type PagedRequest = z.infer<typeof pagedRequestSchema>;

// ---------------------------------------------------------------------------
// Error codes — `ErrorCode::as_str` in backend-rs/crates/domain/src/error_code.rs
// ---------------------------------------------------------------------------

export const ErrorCodes = {
  Internal: 'INTERNAL_ERROR',
  InvalidRequest: 'INVALID_REQUEST',
  ValidationError: 'VALIDATION_ERROR',
  Unauthorized: 'UNAUTHORIZED',
  Forbidden: 'FORBIDDEN',
  NotFound: 'NOT_FOUND',
  InvalidCredentials: 'INVALID_CREDENTIALS',
  UserNotFound: 'USER_NOT_FOUND',
  GoogleUser: 'GOOGLE_USER',
  DuplicateEmail: 'DUPLICATE_EMAIL',
  UserLockedOut: 'USER_LOCKED_OUT',
  RequiresTwoFactor: 'REQUIRES_TWO_FACTOR',
  InvalidToken: 'INVALID_TOKEN',
  PasswordResetFailed: 'PASSWORD_RESET_FAILED',
  RegistrationFailed: 'REGISTRATION_FAILED',
  GoogleLoginFailed: 'GOOGLE_LOGIN_FAILED',
  FeatureNotAvailable: 'FEATURE_NOT_AVAILABLE',
  SubscriptionRequired: 'SUBSCRIPTION_REQUIRED',
  FreeLimitReached: 'FREE_LIMIT_REACHED',
  AiProcessingError: 'AI_PROCESSING_ERROR',
  AiServiceUnavailable: 'AI_SERVICE_UNAVAILABLE',
  AiProviderUnauthorized: 'AI_PROVIDER_UNAUTHORIZED',
  RateLimited: 'RATE_LIMITED',
  InvalidFile: 'INVALID_FILE',
  FileTooLarge: 'FILE_TOO_LARGE',
  UnsupportedFileType: 'UNSUPPORTED_FILE_TYPE',
  UnsupportedMediaType: 'UNSUPPORTED_MEDIA_TYPE',
  PortfolioPrivate: 'PORTFOLIO_PRIVATE',
  AgentNotConnected: 'AGENT_NOT_CONNECTED',
} as const;

// Legacy compatibility alias (from src/types/auth.ts)
/** @deprecated Use ApiResponseDto or ApiResponseWithDataDto<T> instead */
export type ApiResponse<T = unknown> = ApiResponseDto & { data?: T };
